import { useMemo, useReducer } from 'react';

import { useNestedLocationStore } from '../../../newStore/nestedLocationStore';
import {
  changeAncestorsSelectionStatus,
  checkAndUpdateAllLocationsSelected,
  clearChildrenSelectStatus,
  convertSelectedLocationsToMap,
  getLocationAncestors,
  handleExcludeLocationFromAllSelected,
  LocationsIndexes,
  unselectLocationAncestors,
} from '../utils/LocationsHelpers';

/**
 * The status of a location can be:
 * - selected: the location is selected and all its children are selected
 * - excluded: the location is excluded and all its children are excluded
 * - onlyChildren: the location is not selected but all its children are selected by default
 * - undefined: the location is not explicitly selected or excluded, but it can be selected by default if all its ancestors are selected
 *
 * Useful to manage multiple selections.
 */
export type SelectedStatus = 'selected' | 'excluded' | 'onlyChildren' | undefined;
export type SelectedLocations = Map<number, SelectedStatus>;
export type LocationType = 'NestableLocation' | 'Location';

export interface LocationsState {
  selectedLocations: SelectedLocations;
  allLocationsSelected: boolean;
  legacySelectedLocations: number[];
  autoSelectChildren: boolean;
}

interface UpdateLocationSelectionAction {
  type: 'update_location_selection';
  payload: {
    locationId: number | undefined;
    locationType: LocationType;
    multipleSelection?: boolean;
    onlyChildrenAllowed?: boolean;
  };
}

interface ToggleAllLocationsSelectionAction {
  type: 'toggle_all_locations_selection';
}

interface ResetLocationsStateAction {
  type: 'reset_locations_state';
  payload?: {
    selectedLocations?: Record<string | number, SelectedStatus>;
    allLocationsSelected?: boolean;
    legacySelectedLocations?: number[];
  };
}

export type LocationsAction =
  | UpdateLocationSelectionAction
  | ToggleAllLocationsSelectionAction
  | ResetLocationsStateAction;

interface LocationsActionMetadata {
  locationsIndex: LocationsIndexes;
}

type LocationsActionWithMetadata = LocationsAction & { metadata: LocationsActionMetadata };

const getDefaultLocationsState = (
  initialSelectedLocations?: Record<string, SelectedStatus>,
  initialAllLocationsSelected?: boolean,
  initialLegacySelectedLocations?: number[],
  initialAutoSelectChildren?: boolean,
): LocationsState => ({
  selectedLocations: convertSelectedLocationsToMap(initialSelectedLocations ?? {}),
  allLocationsSelected: initialAllLocationsSelected ?? false,
  legacySelectedLocations: initialLegacySelectedLocations ?? [],
  autoSelectChildren: initialAutoSelectChildren ?? true,
});

const createStateSnapshot = (state: LocationsState): LocationsState => {
  return {
    ...state,
    selectedLocations: new Map(state.selectedLocations),
    legacySelectedLocations: [...state.legacySelectedLocations],
  };
};

/**
 * Toggles the selection status of a specific location
 * At the same time it change the selection status of the location and its ancestors or parents.
 * It also clear the selection status of all children of the location.
 */
export const toggleMultipleLocationSelectionAction = (
  state: LocationsState,
  locationId: number,
  { locationsIndex: { locationById, childrenByParentId } }: LocationsActionMetadata,
) => {
  const newState = createStateSnapshot(state);

  const location = locationById.get(locationId);
  if (!location) return state;

  if (!state.autoSelectChildren) {
    if (newState.selectedLocations.get(location.id) === 'selected') {
      newState.selectedLocations.delete(location.id);
    } else {
      newState.selectedLocations.set(location.id, 'selected');
    }
    return newState;
  }

  const ancestors = getLocationAncestors(location.id, locationById);
  const locationSelectedStatus = newState.selectedLocations.get(location.id);

  const atLeastOneAncestorSelected = ancestors.some((ancestor) =>
    ['selected', 'onlyChildren'].includes(newState.selectedLocations.get(ancestor.id)),
  );
  const noAncestorExcluded = ancestors.every(
    (ancestor) => newState.selectedLocations.get(ancestor.id) !== 'excluded',
  );

  const isSelected =
    (!['excluded', 'onlyChildren'].includes(locationSelectedStatus) &&
      atLeastOneAncestorSelected &&
      noAncestorExcluded) ||
    locationSelectedStatus === 'selected' ||
    state.allLocationsSelected;

  clearChildrenSelectStatus(
    location.id,
    locationById,
    childrenByParentId,
    newState.selectedLocations,
  );

  if (isSelected) {
    newState.selectedLocations.set(location.id, 'excluded');
    unselectLocationAncestors(location.id, newState.selectedLocations, locationById);
    if (newState.allLocationsSelected) {
      handleExcludeLocationFromAllSelected(
        location,
        newState.selectedLocations,
        locationById,
        childrenByParentId,
      );
    }
    newState.allLocationsSelected = false;
  } else {
    newState.selectedLocations.set(location.id, 'selected');
    changeAncestorsSelectionStatus(
      location.parent_id,
      newState.selectedLocations,
      locationById,
      childrenByParentId,
    );
    newState.allLocationsSelected = checkAndUpdateAllLocationsSelected(
      newState.selectedLocations,
      locationById,
    );
  }
  return newState;
};

const toggleMultipleLegacyLocationSelectionAction = (state: LocationsState, locationId: number) => {
  const newState = createStateSnapshot(state);
  const isCurrentlySelected = newState.legacySelectedLocations.includes(locationId);
  if (isCurrentlySelected) {
    newState.legacySelectedLocations = newState.legacySelectedLocations.filter(
      (id) => id !== locationId,
    );
  } else {
    newState.legacySelectedLocations.push(locationId);
  }
  return newState;
};

const toggleAllLocationsSelectionAction = (state: LocationsState) => {
  const newState = createStateSnapshot(state);
  if (!newState.allLocationsSelected) {
    newState.selectedLocations.clear();
  }
  newState.allLocationsSelected = !newState.allLocationsSelected;
  return newState;
};

const resetLocationsStateAction = (
  state: LocationsState,
  payload?: ResetLocationsStateAction['payload'],
): LocationsState => {
  const newState = createStateSnapshot(state);
  const { selectedLocations, legacySelectedLocations } = payload || {};
  if (selectedLocations) {
    newState.selectedLocations = convertSelectedLocationsToMap(selectedLocations);
  }
  if (legacySelectedLocations) {
    newState.legacySelectedLocations = legacySelectedLocations;
  }
  return newState;
};

const toggleSingleLocationSelectionAction = (state: LocationsState, locationId: number) => {
  const newState = createStateSnapshot(state);
  const isCurrentlySelected = newState.selectedLocations.get(locationId) === 'selected';
  newState.selectedLocations.clear();
  if (!isCurrentlySelected) {
    newState.selectedLocations.set(locationId, 'selected');
  }
  newState.legacySelectedLocations = [];
  return newState;
};

const toggleSingleLegacyLocationSelectionAction = (state: LocationsState, locationId: number) => {
  const newState = createStateSnapshot(state);
  const isCurrentlySelected = newState.legacySelectedLocations.includes(locationId);
  if (isCurrentlySelected) {
    newState.legacySelectedLocations = [];
  } else {
    newState.legacySelectedLocations = [locationId];
  }
  newState.selectedLocations.clear();
  return newState;
};

const locationsReducer = (state: LocationsState, action: LocationsActionWithMetadata) => {
  switch (action.type) {
    case 'update_location_selection': {
      if (!action.payload.locationId) return state;

      if (action.payload.locationType === 'NestableLocation') {
        if (action.payload.multipleSelection) {
          return toggleMultipleLocationSelectionAction(
            state,
            action.payload.locationId,
            action.metadata,
          );
        } else {
          return toggleSingleLocationSelectionAction(state, action.payload.locationId);
        }
      } else {
        if (action.payload.multipleSelection) {
          return toggleMultipleLegacyLocationSelectionAction(state, action.payload.locationId);
        } else {
          return toggleSingleLegacyLocationSelectionAction(state, action.payload.locationId);
        }
      }
    }
    case 'toggle_all_locations_selection': {
      return toggleAllLocationsSelectionAction(state);
    }
    case 'reset_locations_state': {
      return resetLocationsStateAction(state, action.payload);
    }
    default:
      return state;
  }
};

type MultipleSelectionProps = {
  multipleSelection: true;
  autoSelectChildren?: boolean;
};

type SingleSelectionProps = {
  multipleSelection?: false;
  autoSelectChildren?: never;
};

type SelectionProps = MultipleSelectionProps | SingleSelectionProps;

type UseLocationsSelectorStateProps = {
  initialSelectedLocations?: Record<string | number, SelectedStatus>;
  initialAllLocationsSelected?: boolean;
  initialLegacySelectedLocations?: number[];
} & SelectionProps;

/**
 * Reducer to manage the state of the complex state of the locations selector.
 * @param initialSelectedLocations - Default {}, only used when the component is mounted.
 * @param initialAllLocationsSelected - Default false, only used when the component is mounted.
 * @param initialLegacySelectedLocations - Default [], only used when the component is mounted.
 * @param multipleSelection - Default false, whether the multiple selection is enabled.
 * @param autoSelectChildren - Only available if multipleSelection is true. If true, when a location is selected, all its children are selected.
 * @returns The locations selector state and the functions to manage the state.
 */
export const useLocationsSelectorState = (config?: UseLocationsSelectorStateProps) => {
  const {
    initialSelectedLocations = {},
    initialAllLocationsSelected = false,
    initialLegacySelectedLocations = [],
    multipleSelection = false,
    autoSelectChildren = true,
  } = config ?? {};
  const { locationsIndex } = useNestedLocationStore();

  const [locationsState, dispatch] = useReducer(
    locationsReducer,
    getDefaultLocationsState(
      initialSelectedLocations,
      initialAllLocationsSelected,
      initialLegacySelectedLocations,
      autoSelectChildren,
    ),
  );

  const handleChangeLocationSelection = (locationId: number, locationType: LocationType) => {
    dispatch({
      type: 'update_location_selection',
      payload: { locationId, locationType, multipleSelection },
      metadata: { locationsIndex },
    });
  };

  const handleChangeAllLocationsSelection = () => {
    dispatch({
      type: 'toggle_all_locations_selection',
      metadata: { locationsIndex },
    });
  };

  const handleResetLocationsState = (payload?: ResetLocationsStateAction['payload']) => {
    dispatch({
      type: 'reset_locations_state',
      payload,
      metadata: { locationsIndex },
    });
  };

  const selectedLocation: { id: number; type: LocationType } | undefined = useMemo(() => {
    if (!multipleSelection) {
      if (locationsState.selectedLocations.size > 0) {
        return {
          id: Array.from(locationsState.selectedLocations.keys())[0],
          type: 'NestableLocation',
        };
      }
      if (locationsState.legacySelectedLocations.length > 0) {
        return { id: locationsState.legacySelectedLocations[0], type: 'Location' };
      }
    }
    return undefined;
  }, [locationsState.selectedLocations, locationsState.legacySelectedLocations, multipleSelection]);

  return {
    locationsState,
    handleChangeLocationSelection,
    handleChangeAllLocationsSelection,
    handleResetLocationsState,
    selectedLocation,
  };
};
